import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const rejectReasonSchema = z.object({
  reason: z
    .string()
    .max(1000, "Use 1,000 characters or fewer.")
    .refine((value) => value.trim().length > 0, {
      message: "Enter a reason for rejection.",
    }),
});

type RejectReasonDraft = z.infer<typeof rejectReasonSchema>;

export function RejectDialog({
  open,
  pending,
  failed,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { register, watch, handleSubmit, reset } = useForm<RejectReasonDraft>({
    resolver: zodResolver(rejectReasonSchema, undefined, { mode: "sync" }),
    mode: "onChange",
    defaultValues: { reason: "" },
  });
  const reason = watch("reason");
  const validation = rejectReasonSchema.safeParse({ reason });
  const reasonError = validation.success
    ? undefined
    : validation.error.issues[0]?.message;

  useEffect(() => {
    if (!open && !pending) reset({ reason: "" });
  }, [open, pending, reset]);

  function dismiss() {
    if (!pending) onCancel();
  }

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullWidth
      maxWidth="sm"
      aria-labelledby="reject-application-title"
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit((draft) => onConfirm(draft.reason.trim()))}
      >
        <DialogTitle id="reject-application-title">
          Reject application
        </DialogTitle>
        <DialogContent>
          <TextField
            {...register("reason")}
            autoFocus
            fullWidth
            multiline
            minRows={3}
            disabled={pending}
            label="Reason for rejection"
            placeholder="Reason for rejection..."
            error={reason.length > 0 && !validation.success}
            helperText={reason.length > 0 ? reasonError : undefined}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
          {failed && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              The rejection could not be submitted. Review the reason and try
              again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            type="button"
            onClick={dismiss}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            type="submit"
            disabled={pending || !validation.success}
            sx={{ "&&": { pointerEvents: "auto" } }}
          >
            {pending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
